'use client';

import React, { useState } from 'react';
import classNames from 'classnames';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { Author } from '@benzinga/article-manager';
import { AuthorSocialMedia, AuthorSocialMediaLinksI } from './AuthorSocialMedia';
import { truncate } from '@benzinga/utils';
import { FollowAuthorButton } from './FollowAuthorButton';
import styles from './AuthorInfo.module.scss';

interface AuthorInfoProps {
  author: Author;
}

export const AuthorInfo: React.FC<AuthorInfoProps> = ({ author }) => {
  const [isShowingMore, setIsShowingMore] = useState(false);

  const handleShowMore = () => setIsShowingMore(!isShowingMore);

  const social: AuthorSocialMediaLinksI = {
    facebook: author.facebook,
    instagram: author.instagram,
    linkedin: author.linkedin,
    threads: author.threads,
    tiktok: author.tiktok,
    twitter: author.twitter,
    uid: author.uid,
  };

  return (
    <div className={classNames(styles.wrapper, 'author-info')}>
      <div className="grid grid-cols-1 gap-5">
        <div className="flex sm:items-center gap-3">
          <div className="avatar">
            <img alt={author.name} className="w-full h-full object-cover object-top rounded" src={author.picture} />
          </div>
          <div className="author-content">
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div>
                <h2 className="text-3xl">{author.name}</h2>
                <p className="mb-0 text">{author.byLine}</p>
                <p className="mb-2 text font-xs">{author.byLineSecondary}</p>
                <div className="flex gap-3">
                  <FollowAuthorButton authorName={author.name} authorUid={author.uid} variant="large" />
                  <AuthorSocialMedia social={social} />
                </div>
              </div>
            </div>
          </div>
        </div>
        {author.about && (
          <div>
            <h3 className="heading">About {author.name}</h3>
            <div className="flex flex-col gap-2 mb-4">
              <div
                className="text-sm"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHTML(isShowingMore ? author.about : truncate(author.about, 250)),
                }}
              />
              {author.about.length > 470 && (
                <button className="text-blue-400 text-sm inline-flex" onClick={() => handleShowMore()}>{`Show ${
                  isShowingMore ? 'Less' : 'More'
                }`}</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthorInfo;
